/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";

import { TokenManager } from "../../../model/auth";
import { Log } from "../../../model/logging";
import { sessionInvalidated } from "../../signIn/signIn.actions";

const TAG: string = "profileDeactivationThunks";

/**
 * Invalidates a deleted profile's authentication context. If the deleted
 * profile is active, also returns the app to the account context.
 *
 * Deleting the profile drops its token from memory and from IndexedDB. The
 * account token takes over only when the deleted profile was active; deleting
 * another profile leaves the current authentication context unchanged.
 *
 * Only a failure to restore the account context is terminal, and that leaves
 * the session unauthenticated but recoverable.
 */
export const deactivateProfileAuthentication = createAsyncThunk<void, string>(
  "profiles/deactivateProfileAuthentication",
  async (profileId, { dispatch }) => {
    const isActiveProfile: boolean =
      TokenManager.getActiveProfileId() === profileId;

    try {
      await TokenManager.clearContext(profileId);
    } catch (profileTokenError: unknown) {
      Log.e(
        TAG,
        "deactivateProfileAuthentication() profile token cleanup",
        profileTokenError
      );
    }

    if (!isActiveProfile) {
      return;
    }

    try {
      await TokenManager.setActiveContext(null);
    } catch (accountRecoveryError: unknown) {
      Log.e(
        TAG,
        "deactivateProfileAuthentication() account context restore",
        accountRecoveryError
      );

      dispatch(sessionInvalidated());
    }
  }
);
